import React, { ReactNode } from 'react';

import { Box, CircularProgress, Typography, useTheme } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import { CompanyWorkerItem } from '@vyce/core/src/types';

const LOAD_MORE_THRESHOLD = 120;
const SKELETON_ROWS_COUNT = 5;

interface Props {
  workers: CompanyWorkerItem[];
  loading: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  renderRow: (worker: CompanyWorkerItem) => ReactNode;
  maxHeight?: number | string;
  /**
   * Fixed height instead of a cap — the area then stays the same size across loading,
   * empty and full states, so a dialog around it doesn't jump when a search reloads.
   */
  height?: number | string;
  emptyText?: string;
}

/** Scroll container for a workers picker: skeletons while loading, load-more near the bottom. */
export const WorkersScrollList: React.FC<Props> = ({
  workers,
  loading,
  loadingMore,
  onLoadMore,
  renderRow,
  maxHeight = 360,
  height,
  emptyText = 'No users found.',
}) => {
  const theme = useTheme();

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < LOAD_MORE_THRESHOLD) {
      onLoadMore();
    }
  };

  return (
    <Box
      height={height}
      maxHeight={height ?? maxHeight}
      style={{ overflowY: 'auto' }}
      onScroll={handleScroll}
    >
      <Box display="flex" flexDirection="column" gridGap={4}>
        {loading ? (
          Array.from({ length: SKELETON_ROWS_COUNT }).map((_, index) => (
            <Box key={index} display="flex" alignItems="center" gridGap={12} padding="6px 8px">
              <Skeleton
                variant="circle"
                width={40}
                height={40}
                style={{ backgroundColor: theme.palette.action.hover }}
              />
              <Skeleton variant="text" width="40%" style={{ backgroundColor: theme.palette.action.hover }} />
            </Box>
          ))
        ) : !workers.length ? (
          <Typography color="textSecondary">{emptyText}</Typography>
        ) : (
          workers.map(renderRow)
        )}

        {loadingMore && (
          <Box display="flex" justifyContent="center" padding={1}>
            <CircularProgress color="secondary" size={20} />
          </Box>
        )}
      </Box>
    </Box>
  );
};
